import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Auth.css";

export default function ForgotPassword() {
  // ── OTP step disabled — email sending is currently blocking on the
  // server, so this goes straight from "enter email" (step 1) to
  // "set new password" (step 3, kept numbered as 3 for minimal diff in
  // case OTP is re-enabled later). Step 2 (OTP entry) is skipped. ────
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Confirms the email is registered, then skips straight to step 3
  // (set new password) without sending/verifying an OTP.
  const confirmEmail = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");

    setLoading(true);

    try {
      const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/get-user?email=${encodeURIComponent(email)}`);
      if (!res.ok) {
        toast.error("Email not registered");
        setLoading(false);
        return;
      }
      setStep(3);
    } catch {
      toast.error("Error checking email");
    } finally {
      setLoading(false);
    }
  };

  /* ── OTP request/verify disabled ───────────────────────────────────
  const requestOtp = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");

    setLoading(true);

    try {
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("OTP sent successfully to your email");
        setStep(2);
      } else {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error("Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) return toast.error("Please enter the OTP");

    setLoading(true);

    try {
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: otp }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("OTP verified successfully");
        setStep(3);
      } else {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error("Error verifying OTP");
    } finally {
      setLoading(false);
    }
  };
  ──────────────────────────────────────────────────────────────────── */

  const resetPassword = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword)
      return toast.error("Please fill both password fields");

    if (newPassword !== confirmPassword)
      return toast.error("Passwords do not match");

    setLoading(true);

    try {
      const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, newPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        toast.success("Password reset successfully. Please log in with your new password.");
        navigate("/");
      } else {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error("Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <div className="auth-image-section">
        <img 
          src={`${process.env.PUBLIC_URL}/mf20.png`} 
          alt="MF20 Antibody - Myosin Heavy Chain" 
          className="antibody-image"
        />
        <div className="image-overlay"></div>
        <div className="image-label">

            <a href="https://sb3-dshb.biology.uiowa.edu/MF-20"
            target="_blank"
            rel="noopener noreferrer"
            className="label-antibody-link"
          >
            <p className="label-title">MF 20 ↗</p>
          </a>
          <p className="label-subtitle">Sarcomeric myosin heavy chain (MYH1E)</p>
          <div className="label-meta">
            <p className="label-depositor">Depositor: D.A Fischman (1986)</p>
          </div>
        </div>
      </div>

      <div className="auth-form-section">
      <div className="auth-container">
        <div className="back-icon" onClick={() => {
          if (step === 1) navigate("/");
          else setStep(1);
        }}>←</div>

        <div className="auth-header">
          <div className="header-with-logo">
              <img src={`${process.env.PUBLIC_URL}/dshb-logo.png`} alt="DSHB Logo" className="header-logo" />
              <div className="header-text">
                <h1 className="portal-title">DSHB Depositor Portal</h1>
              </div>
            </div>
            <p>Reset Password</p>
        </div>

        <div className="step-indicator">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            {step > 1 ? '✓' : '1'}
          </div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>2</div>
        </div>

        {step === 1 && (
          <form onSubmit={confirmEmail}>
            <div className="form-group">
              <label>Email Address <span className="required">*</span></label>
              <input
                type="email"
                placeholder="Enter your registered email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? "Checking..." : "Continue"}
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={resetPassword}>
            <div className="form-group password-field">
              <label>New Password <span className="required">*</span></label>
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="Create a new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
              />
              <span
                className="eye-icon"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="form-group password-field">
              <label>Confirm Password <span className="required">*</span></label>
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
              <span
                className="eye-icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            {confirmPassword && (
              <div className={`password-match ${newPassword === confirmPassword ? 'match' : 'nomatch'}`}>
                {newPassword === confirmPassword
                  ? "✓ Passwords match"
                  : "✗ Passwords do not match"}
              </div>
            )}

            <button type="submit" disabled={loading || newPassword !== confirmPassword}>
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}
      </div>
      </div>
    </div>
  );
}